import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { HeaderItem } from '@juvare/common-web-ui/GlobalApp';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { ConfigurationsPage } from '@juvare/platform-sdk-react/ui/features/classifiers';
import { ConfigurableListTable } from '@juvare/platform-sdk-react/ui/features/configurable-lists';
import { EditablePagesPage } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { EditableSettingsAdminPage } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { getLinkToIdentityProviderProfile } from '@juvare/platform-sdk-react/ui/utils';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, RouteObject } from 'react-router';
import {
    configurableListsRoutes,
    configurationsLinks,
    editablePagesLinks,
    settingsLinks,
    settingsRoutes as settingsRoutesData,
} from '../../backend/config/routes';
import { DependenciesVersionsTable } from '../components/DependenciesVersionsTable';
import { useConfigurationPages } from '../features/classifiers/utils/useConfigurationPages';
import { getConfigurationPagesByPermission } from '../utils/getConfigurationPagesByPermission';

interface Result {
    settingsRoutes: RouteObject[];
    settingsHeaderItems: HeaderItem[];
}

interface MenuItems {
    label: string;
    navigateTo?: string;
    onClick?: (navigate: NavigateFunction) => void;
    disabled?: boolean;
    'data-test'?: string;
}

export function useSettingsMenu(): Result {
    const { t } = useTranslation('healthAlerts');
    const userState = useUserState();
    const settingsRoutes: RouteObject[] = [];
    const settingsHeaderItems: HeaderItem[] = [];
    const menuItems: MenuItems[] = [];
    const configPages = useConfigurationPages(userState);

    menuItems.push({
        label: t('appHeader.settings.oktaRedirect'),
        onClick: () => {
            window.open(getLinkToIdentityProviderProfile(), '_blank');
        },
        'data-test': 'toolbar-okta-redirect-item',
    });

    if (requireAnySystemPermission(userState, 'manage-all-classifiers', 'manage-credentialing-classifiers', 'manage-client-classifiers')) {
        settingsRoutes.push({
            path: 'configurations/*',
            element: <ConfigurationsPage pages={getConfigurationPagesByPermission(userState, configPages)} />,
        });

        configPages.forEach((page) => {
            const Component = page.component;
            settingsRoutes.push({
                path: 'configurations/' + page.path + '/*',
                element: <Component />,
            });
        });

        menuItems.push({
            label: t('appHeader.settings.configurations'),
            navigateTo: configurationsLinks.root,
            'data-test': 'toolbar-configuration-item',
        });
    }

    if (requireAnySystemPermission(userState, 'manage-configurable-lists')) {
        menuItems.push({
            label: t('appHeader.settings.configurableLists'),
            navigateTo: configurableListsRoutes.root,
            'data-test': 'toolbar-configurable-lists-item',
        });
        settingsRoutes.push({
            path: configurableListsRoutes.root,
            element: <ConfigurableListTable entity='configurableList' canCreate />,
        });
    }

    if (requireAnySystemPermission(userState, 'manage-settings')) {
        menuItems.push({
            label: t('appHeader.settings.settings'),
            navigateTo: settingsLinks.root,
            'data-test': 'toolbar-settings-item',
        });
        settingsRoutes.push({
            path: settingsRoutesData.root,
            element: <EditableSettingsAdminPage />,
        });
    }

    if (requireAnySystemPermission(userState, 'manage-editable-pages')) {
        settingsRoutes.push({
            path: 'editable-pages/*',
            element: (
                <EditablePagesPage
                    title={t('appHeader.settings.supportPages')}
                    // TODO: use new permission framework with CNEO-3859
                    canCreate={requireAnySystemPermission(userState, 'manage-editable-pages')}
                    canDelete={requireAnySystemPermission(userState, 'manage-editable-pages')}
                    tags={[
                        {
                            value: 'landingPage',
                            label: t('editablePagesTags.loginLinks'),
                        },
                        {
                            value: 'supportCenter',
                            label: t('editablePagesTags.supportCenter'),
                        },
                        {
                            value: 'login',
                            label: t('editablePagesTags.loginMessage'),
                        },
                    ]}
                />
            ),
        });
        settingsHeaderItems.push({
            icon: <SmsFailedOutlinedIcon />,
            label: t('appHeader.settings.supportPages'),
            navigateTo: editablePagesLinks.root,
            'data-test': 'toolbar-support-pages-item',
        });
    }

    if (requireAnySystemPermission(userState, 'view-versions')) {
        menuItems.push(
            {
                label: t('panels.home.versionInformation'),
                onClick: () => {
                    showAlertDialog({
                        title: t('panels.home.versionInformation'),
                        content: <DependenciesVersionsTable />,
                    });
                },
                'data-test': 'toolbar-versions-item',
            },
            {
                label: t('panels.home.userState'),
                onClick: () =>
                    showAlertDialog({
                        title: t('panels.home.userState'),
                        content: <pre>{JSON.stringify(userState, undefined, '  ')}</pre>,
                    }),
                'data-test': 'toolbar-user-state-item',
            },
        );
    }

    if (menuItems.length) {
        settingsHeaderItems.push({
            icon: <MoreHorizIcon />,
            label: 'More',
            menuItems,
            'data-test': 'settings-button',
        });
    }

    return { settingsRoutes, settingsHeaderItems };
}
